import { Button, Flex } from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { DataTableSortStatus } from "mantine-datatable";
import { useCallback, useState } from "react";

import { useCreateTenant } from "../../api/hooks/tenant/useCreateTenant.ts";
import { useTenants } from "../../api/hooks/tenant/useTenants.ts";
import { useUpdateTenant } from "../../api/hooks/tenant/useUpdateTenant.ts";
import { useUpdateTenantStatus } from "../../api/hooks/tenant/useUpdateTenantStatus.ts";
import SvgPlus from "../../components/Icons/Plus";
import Layout from "../../components/Layout/Layout";
import Preloader from "../../components/Preloader/Preloader.tsx";
import Table from "../../components/Table/Table";
import { SortDirection } from "../../components/Table/types.ts";
import Title from "../../components/Title/Title.tsx";
import {
  CreateTenantInput,
  OrderBy,
  TenantOrderBy,
  UpdateTenantStatusInput,
} from "../../graphql/generated";
import { useMutationNotificationWrapper } from "../../hooks/useMutationNotificationWrapper.tsx";
import { Tenant } from "../../types/tenant.ts";
import { getApiOrderBy } from "../../utils/api";
import { getPagesCount } from "../../utils/pagination.ts";
import CreateTenantModal from "./CreateTenantModal/CreateTenantModal.tsx";
import { useTenantsTableData } from "./hooks/useTenantsTableData.tsx";

export default function TenantManagementPage() {
  const [selectedTenant, setSelectedTenant] = useState<Tenant | null>(null);

  const [
    tenantModalOpened,
    { open: tenantModalOpen, close: tenantModalClose },
  ] = useDisclosure(false);

  const [orderBy, setOrderBy] = useState<TenantOrderBy>({
    createdAt: OrderBy.Desc,
  });
  const [page, setPage] = useState<number>(1);
  const [{ data, fetching }, refreshTenants] = useTenants({
    orderBy,
    page,
  });
  const tenants = data?.tenants.data;

  const [, createTenant] = useMutationNotificationWrapper(useCreateTenant(), {
    success: { message: "Tenant has been added to the list." },
  });
  const [, updateTenant] = useMutationNotificationWrapper(useUpdateTenant(), {
    success: { message: "Tenant has been updated." },
  });
  const [, updateTenantStatus] = useMutationNotificationWrapper(
    useUpdateTenantStatus(),
    {
      success: { message: "Tenant status has been updated." },
    }
  );

  const handleUpdateTenantStatus = useCallback(
    (id: number, input: UpdateTenantStatusInput) => {
      updateTenantStatus({ id, input }).then(refreshTenants);
    },
    [refreshTenants, updateTenantStatus]
  );

  const handleOpenEditTenantModal = (id: number) => {
    const tenant = tenants?.find((item) => item.id === id);
    if (!tenant) {
      return;
    }
    setSelectedTenant(tenant);
    tenantModalOpen();
  };

  const [columns, rows] = useTenantsTableData({
    data: tenants,
    onInlineEdit: handleUpdateTenantStatus,
    onEdit: handleOpenEditTenantModal,
  });

  const pageCount = getPagesCount(data?.tenants.total);

  const handleSortChange = useCallback(
    (sort: DataTableSortStatus) => {
      setOrderBy(getApiOrderBy(sort));
    },
    [setOrderBy]
  );

  const handlePageChange = useCallback(
    (page: number) => {
      setPage(page);
    },
    [setPage]
  );

  const handleCreateOrUpdateTenant = useCallback(
    (input: CreateTenantInput) => {
      const handler = selectedTenant
        ? updateTenant({ id: selectedTenant.id, input })
        : createTenant({ input });
      handler.then(refreshTenants);
    },
    [createTenant, refreshTenants, selectedTenant, updateTenant]
  );

  const handleCloseTenantModal = () => {
    setSelectedTenant(null);
    tenantModalClose();
  };

  return (
    <Layout>
      <Preloader loading={fetching} />
      <Flex direction="column" gap={{ base: 24, sm: 20 }}>
        <Flex gap={20} justify="space-between">
          <Flex gap={20}>
            <Title size="h1">Tenant Management</Title>
          </Flex>
        </Flex>
        <Button
          uppercase
          variant="filledGolden"
          leftIcon={<SvgPlus />}
          onClick={tenantModalOpen}
        >
          Add
        </Button>
        {rows && (
          <Table
            columns={columns}
            rows={rows}
            pagination={{
              pageCount: pageCount,
              page: page,
              onPageChange: handlePageChange,
            }}
            defaultSort={{
              columnAccessor: "createdAt",
              direction: SortDirection.asc,
            }}
            onSortChange={handleSortChange}
          />
        )}
        {tenantModalOpened && (
          <CreateTenantModal
            opened={true}
            data={selectedTenant}
            onSubmit={handleCreateOrUpdateTenant}
            onClose={handleCloseTenantModal}
          />
        )}
      </Flex>
    </Layout>
  );
}
